/**
 * OverviewRoute — Hjem, indeksen `/` og Hjem-fanen.
 *
 * #154 (redesign steg 4a, design/screens/Stell_Hjem.dc.html): Hjem har fått
 * sin egen telefonlayout i én kolonne, ovenfra og ned:
 *   1. toppen: avataren (til Meg), «Hei, {navn}!», dato og uke, og
 *      streak-chipen;
 *   2. Dagens hilsen (BriefingTile);
 *   3. «I dag»: datopanelet og dagens hendelser (TodayTile) → /kalender;
 *   4. minifliser: Saldo → /poeng, Ukas leder → /leaderboard,
 *      Middag → /meny;
 *   5. siste innslag i familieflyten → /familie.
 *
 * Skallet legger ikke sin egen topp over Hjem (`drawsOwnHeader`), så
 * avataren står bare én gang.
 *
 * Kjøkkenskjermen (IdleMode) bruker DateTile, EventsTile, OpenChoresTile,
 * WeatherTile og HomeStatusStrip. De restyles ikke her og står ikke lenger på
 * Hjem: vær og hjemmestatus er bare for kjøkkenet (MAPPING.md), og ledertavla
 * fra OpenChoresTile er nå «Ukas leder». Kjøkkenets utseende er steg 10.
 *
 * Uten innlogget bruker (kjøkkenet vekket uten økt, DASH-06) vises ingen
 * personlige kort: ingen hilsen med navn, ingen streak, ingen Dagens hilsen og
 * ingen saldo. Fellesflisene står, og viser tomtilstanden når API-et svarer 401.
 *
 * Utelatt til det er bekreftet (PLAN.md 4a): «Mine oppgaver · N av M» (ingen
 * spørring gir gjort/totalt) og allergenchipen på middag (ingen data).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MeAvatarLink } from '../../components/ScreenHeader';
import { FeedbackWidget } from '../feedback/FeedbackWidget';
import { FlameChip } from '../../components/primitives/FlameChip';
import { capitalizeFirst, formatDate, formatWeekNumber } from '../../i18n/formatters';
import { useSession, type SessionMember } from '../auth/useSession';
import { useStreak } from '../streaks/useStreakQueries';
import { BalanceTile } from './tiles/BalanceTile';
import { BriefingTile } from './tiles/BriefingTile';
import { useToday } from './tiles/DateTile';
import { FeedTile } from './tiles/FeedTile';
import { LeaderTile } from './tiles/LeaderTile';
import { MenyTile } from './tiles/MenyTile';
import { TodayTile } from './tiles/TodayTile';

function HomeStreak({ memberId }: { memberId: string }): ReactNode {
  const { data } = useStreak(memberId);
  const days = data?.current_streak ?? 0;
  if (days <= 0) return null;
  // briefing-streak sto før under Dagens hilsen; streaken står nå i toppen.
  return (
    <span data-testid="briefing-streak" className="shrink-0">
      <FlameChip days={days} size="md" />
    </span>
  );
}

function HomeHeader({
  member,
  today,
}: {
  member: SessionMember | undefined;
  today: Date;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <header data-testid="home-header" className="flex items-center gap-3">
      <MeAvatarLink />
      <div className="min-w-0 flex-1">
        <h1 className="m-0 truncate text-xl font-extrabold tracking-tight text-ink">
          {member ? t('home.greeting', { name: member.short_name }) : t('nav.home')}
        </h1>
        <p data-testid="home-date" className="m-0 mt-0.5 truncate text-control text-ink-2">
          {t('dashboard.date.composed', {
            date: capitalizeFirst(formatDate(today)),
            week: formatWeekNumber(today),
          })}
        </p>
      </div>
      {member ? <HomeStreak memberId={member.id} /> : null}
      {/* #187: tilbakemelding fra Hjem, med bildet av Hjem. */}
      <FeedbackWidget variant="icon" />
    </header>
  );
}

export function OverviewRoute(): ReactNode {
  const { data: session } = useSession();
  // Mens økten lastes regnes det som uten bruker, så ingen personlige kort
  // blinker forbi på en kjøkkenskjerm uten økt.
  const member = session?.member;
  // Én midnattstimer for toppen og «I dag»-flisen, så de alltid viser samme dag.
  const today = useToday();

  return (
    <main data-testid="overview-route" className="min-h-dvh px-5 pt-4 pb-6 md:px-8">
      <div className="mx-auto flex w-full max-w-xl flex-col gap-3">
        <HomeHeader member={member} today={today} />
        {member ? <BriefingTile /> : null}
        <TodayTile today={today} />
        <div className={`grid gap-2.5 ${member ? 'grid-cols-3' : 'grid-cols-2'}`}>
          {member ? <BalanceTile memberId={member.id} /> : null}
          <LeaderTile />
          <MenyTile />
        </div>
        <FeedTile />
      </div>
    </main>
  );
}
